import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ShoppingCart, 
  Package, 
  Truck, 
  CheckCircle, 
  Clock, 
  Search, 
  Filter, 
  Plus, 
  Eye, 
  Edit, 
  Trash2, 
  Check, 
  X, 
  X as CloseIcon,
  Building2,
  User,
  Users,
  Calendar,
  DollarSign,
  FileText,
  Home,
  Phone,
  Mail,
  Tag,
  BarChart3,
  Receipt,
  Layers,
  ShoppingBag,
  ArrowLeft,
  ArrowRight,
  Loader2,
  ArrowLeft as BackIcon,
  CreditCard,
  Calculator
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import Layout from '../components/Layout';
import apiService from '../services/apiService';
import toast from 'react-hot-toast';

const ApprovePurchases = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [purchases, setPurchases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [supplierFilter, setSupplierFilter] = useState('');
  const [suppliers, setSuppliers] = useState([]);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(15);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);



  // Fetch pending purchases
  const fetchPendingPurchases = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: currentPage,
        per_page: perPage,
        status: 'pending',
        ...(searchTerm && { search: searchTerm }),
        ...(supplierFilter && { supplier_id: supplierFilter })
      });

      const response = await apiService.request(`/purchases?${params}`);
      setPurchases(response.data.data);
      setTotalPages(response.data.last_page);
      setTotalItems(response.data.total);
      setError(null);
    } catch (err) {
      setError('Failed to fetch pending purchases');
      console.error('Error fetching pending purchases:', err);
    } finally {
      setLoading(false);
    }
  };

  // Fetch suppliers
  const fetchSuppliers = async () => {
    try {
      const response = await apiService.request('/suppliers');
      setSuppliers(response.data);
    } catch (err) {
      console.error('Error fetching suppliers:', err);
    }
  };

  // Effects
  useEffect(() => {
    fetchPendingPurchases();
  }, [currentPage, perPage, searchTerm, supplierFilter]);

  useEffect(() => {
    fetchSuppliers();
  }, []);

  // Handlers

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handleSupplierFilter = (supplierId) => {
    setSupplierFilter(supplierId);
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handlePerPageChange = (newPerPage) => {
    setPerPage(newPerPage);
    setCurrentPage(1);
  };

  const handleApprovePurchase = async (purchaseId) => {
    try {
      await apiService.request(`/purchases/${purchaseId}/approve`, {
        method: 'POST'
      });
      
      // Refresh the list
      fetchPendingPurchases();
      
      // Show success message
      alert('Purchase approved successfully!');
    } catch (err) {
      console.error('Error approving purchase:', err);
      alert('Failed to approve purchase');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-GH', {
      style: 'currency',
      currency: 'GHS',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  return (
    <Layout>
      <div className="space-y-6">
          {/* Hero Section */}
          <div className="relative overflow-hidden bg-gradient-to-br from-yellow-50 via-white to-orange-50 rounded-2xl lg:rounded-3xl p-4 lg:p-8 mb-6 lg:mb-8 shadow-2xl border border-yellow-200">
            {/* Background Pattern */}
            <div className="absolute inset-0 bg-gradient-to-br from-yellow-50 via-white to-orange-50 opacity-50"></div>
            <div className="absolute top-0 right-0 w-32 h-32 lg:w-48 lg:h-48 bg-gradient-to-br from-yellow-100 to-orange-100 rounded-full -translate-y-16 lg:-translate-y-24 translate-x-16 lg:translate-x-24 opacity-30"></div>
            <div className="absolute bottom-0 left-0 w-24 h-24 lg:w-36 lg:h-36 bg-gradient-to-tr from-orange-100 to-yellow-100 rounded-full translate-y-12 lg:translate-y-18 -translate-x-12 lg:-translate-x-18 opacity-30"></div>
            
            <div className="relative z-10">
              <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4 lg:gap-0">
                <div className="flex flex-col sm:flex-row items-start sm:items-center space-y-4 sm:space-y-0 sm:space-x-6">
                  <div className="relative">
                    <div className="w-12 h-12 lg:w-16 lg:h-16 bg-gradient-to-br from-yellow-500 via-orange-500 to-red-600 rounded-xl lg:rounded-2xl flex items-center justify-center shadow-2xl transform rotate-3 hover:rotate-0 transition-transform duration-300">
                      <CheckCircle size={24} className="lg:w-8 lg:h-8 text-white" />
                    </div>
                    <div className="absolute -top-1 -right-1 w-4 h-4 lg:w-5 lg:h-5 bg-gradient-to-br from-green-400 to-green-600 rounded-full flex items-center justify-center shadow-lg">
                      <Check size={8} className="lg:w-3 lg:h-3 text-white" />
                    </div>
                  </div>
                  <div>
                    <h1 className="text-2xl lg:text-4xl font-bold bg-gradient-to-r from-yellow-600 via-orange-600 to-red-600 bg-clip-text text-transparent mb-1 lg:mb-2">
                      Approve Purchases
                    </h1>
                    <p className="text-gray-600 text-sm lg:text-lg font-medium">
                      Review and approve pending purchase orders
                    </p>
                  </div>
                </div>
                <div className="text-left lg:text-right">
                  <div className="text-2xl lg:text-3xl font-bold text-yellow-600">
                    {purchases.length}
                  </div>
                  <div className="text-xs lg:text-sm text-gray-600">Pending Approvals</div>
                </div>
              </div>
            </div>
          </div>

          {/* Filters and Search */}
          <div className="bg-white rounded-xl lg:rounded-2xl shadow-lg border border-gray-200 p-4 lg:p-6 mb-4 lg:mb-6">
            <div className="flex flex-col lg:flex-row gap-3 lg:gap-4">
              {/* Search */}
              <div className="flex-1">
                <div className="relative">
                  <Search size={20} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search by order number, supplier, or invoice..."
                    value={searchTerm}
                    onChange={handleSearch}
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all duration-200"
                  />
                </div>
              </div>

              {/* Supplier Filter */}
              <div className="lg:w-48">
                <select
                  value={supplierFilter}
                  onChange={(e) => handleSupplierFilter(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all duration-200"
                >
                  <option value="">All Suppliers</option>
                  {suppliers.map((supplier) => (
                    <option key={supplier.id} value={supplier.id}>
                      {supplier.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Clear Filters */}
              <button
                onClick={() => {
                  setSearchTerm('');
                  setSupplierFilter('');
                  setCurrentPage(1);
                }}
                className="px-6 py-3 bg-gradient-to-r from-gray-100 to-gray-200 text-gray-700 rounded-xl hover:from-gray-200 hover:to-gray-300 transition-all duration-200 flex items-center gap-2 font-semibold shadow-sm hover:shadow-md transform hover:scale-105"
              >
                <X size={16} />
                Clear
              </button>
            </div>
          </div>

          {/* Pending Purchases Table */}
          <div className="bg-white rounded-xl lg:rounded-2xl shadow-lg border border-gray-200 overflow-hidden">
            <div className="px-4 lg:px-6 py-3 lg:py-4 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-white">
              <h3 className="text-base lg:text-lg font-semibold text-gray-900">Pending Purchase Orders</h3>
              <p className="text-xs lg:text-sm text-gray-500">Review and approve purchase orders awaiting approval</p>
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="animate-spin text-red-500" size={32} />
                <span className="ml-3 text-gray-600">Loading pending purchases...</span>
              </div>
            ) : error ? (
              <div className="flex items-center justify-center py-12">
                <div className="text-center">
                  <X className="mx-auto text-red-500" size={48} />
                  <p className="mt-2 text-gray-600">{error}</p>
                  <button
                    onClick={fetchPendingPurchases}
                    className="mt-4 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
                  >
                    Retry
                  </button>
                </div>
              </div>
            ) : purchases.length === 0 ? (
              <div className="flex items-center justify-center py-12">
                <div className="text-center">
                  <CheckCircle className="mx-auto text-green-500" size={48} />
                  <p className="mt-2 text-gray-600 text-lg">No pending purchases</p>
                  <p className="text-gray-400">All purchase orders have been approved</p>
                </div>
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gradient-to-r from-gray-50 to-gray-100">
                      <tr>
                        <th className="px-3 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Order #</th>
                        <th className="px-3 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden sm:table-cell">Supplier</th>
                        <th className="px-3 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total Cost</th>
                        <th className="px-3 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden lg:table-cell">Expected Delivery</th>
                        <th className="px-3 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden md:table-cell">Created</th>
                        <th className="px-3 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {purchases.map((purchase) => (
                        <tr key={purchase.id} className="hover:bg-gradient-to-r hover:from-gray-50 hover:to-white transition-all duration-200">
                          <td className="px-3 lg:px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <div className="w-8 h-8 lg:w-10 lg:h-10 bg-gradient-to-br from-yellow-100 to-orange-100 rounded-full flex items-center justify-center">
                                <ShoppingCart size={14} className="lg:w-4 lg:h-4 text-yellow-600" />
                              </div>
                              <div className="ml-2 lg:ml-4">
                                <div className="text-xs lg:text-sm font-medium text-gray-900">{purchase.purchase_order_number}</div>
                                {purchase.supplier_invoice_number && (
                                  <div className="text-xs text-gray-500 hidden sm:block">Invoice: {purchase.supplier_invoice_number}</div>
                                )}
                                <div className="text-xs text-gray-500 sm:hidden">{purchase.supplier?.name || 'N/A'}</div>
                              </div>
                            </div>
                          </td>
                          <td className="px-3 lg:px-6 py-4 whitespace-nowrap hidden sm:table-cell">
                            <div className="text-sm font-medium text-gray-900">{purchase.supplier?.name || 'N/A'}</div>
                            <div className="text-sm text-gray-500">{purchase.contact_person || 'N/A'}</div>
                          </td>
                          <td className="px-3 lg:px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-gray-900">{formatCurrency(purchase.total_cost)}</div>
                          </td>
                          <td className="px-3 lg:px-6 py-4 whitespace-nowrap hidden lg:table-cell">
                            <div className="text-sm text-gray-900">
                              {purchase.expected_delivery_date 
                                ? new Date(purchase.expected_delivery_date).toLocaleDateString()
                                : 'N/A'
                              }
                            </div>
                          </td>
                          <td className="px-3 lg:px-6 py-4 whitespace-nowrap hidden md:table-cell">
                            <div className="text-sm text-gray-900">
                              {new Date(purchase.created_at).toLocaleDateString()}
                            </div>
                          </td>
                          <td className="px-3 lg:px-6 py-4 whitespace-nowrap text-sm font-medium">
                            <div className="flex items-center space-x-2">
                              <button
                                onClick={() => handleCardClick(`/purchases/${purchase.id}`)}
                                className="text-blue-600 hover:text-blue-900 p-2 rounded-lg hover:bg-blue-50 transition-all duration-200"
                                title="View Details"
                              >
                                <Eye size={16} />
                              </button>
                              <button
                                onClick={() => handleApprovePurchase(purchase.id)}
                                className="text-green-600 hover:text-green-900 p-2 rounded-lg hover:bg-green-50 transition-all duration-200"
                                title="Approve Purchase"
                              >
                                <CheckCircle size={16} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                <div className="bg-gradient-to-r from-gray-50 to-white px-4 lg:px-6 py-3 lg:py-4 border-t border-gray-200">
                  <div className="flex flex-col lg:flex-row items-center justify-between gap-3 lg:gap-0">
                    <div className="flex flex-col sm:flex-row items-center space-y-2 sm:space-y-0 sm:space-x-4">
                      <span className="text-xs lg:text-sm text-gray-700 text-center sm:text-left">
                        Showing {((currentPage - 1) * perPage) + 1} to {Math.min(currentPage * perPage, totalItems)} of {totalItems} results
                      </span>
                      <select
                        value={perPage}
                        onChange={(e) => handlePerPageChange(Number(e.target.value))}
                        className="px-2 lg:px-3 py-1 border border-gray-300 rounded-lg text-xs lg:text-sm focus:ring-2 focus:ring-red-500 focus:border-transparent"
                      >
                        <option value={10}>10 per page</option>
                        <option value={15}>15 per page</option>
                        <option value={25}>25 per page</option>
                        <option value={50}>50 per page</option>
                      </select>
                    </div>
                    <div className="flex items-center space-x-1 lg:space-x-2">
                      <button
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                        className="px-3 lg:px-4 py-2 border border-gray-300 rounded-lg lg:rounded-xl text-xs lg:text-sm font-medium text-gray-700 hover:bg-gradient-to-r hover:from-gray-50 hover:to-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                      >
                        <ArrowLeft size={14} className="lg:w-4 lg:h-4 inline mr-1" />
                        <span className="hidden sm:inline">Previous</span>
                        <span className="sm:hidden">Prev</span>
                      </button>
                      <span className="px-3 lg:px-4 py-2 text-xs lg:text-sm text-gray-700 bg-gradient-to-r from-gray-100 to-gray-200 rounded-lg lg:rounded-xl font-medium">
                        Page {currentPage} of {totalPages}
                      </span>
                      <button
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === totalPages}
                        className="px-3 lg:px-4 py-2 border border-gray-300 rounded-lg lg:rounded-xl text-xs lg:text-sm font-medium text-gray-700 hover:bg-gradient-to-r hover:from-gray-50 hover:to-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                      >
                        <span className="hidden sm:inline">Next</span>
                        <span className="sm:hidden">Next</span>
                        <ArrowRight size={14} className="lg:w-4 lg:h-4 inline ml-1" />
                      </button>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
    </Layout>
  );
};

export default ApprovePurchases;
